import React, { useMemo, useCallback, forwardRef, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';

import { useHideModal } from '../../actions/modal';
import { showConfirmDialog } from '../../actions/messageDialog';
import { DateTimePicker } from '../../components/DateTimePicker/DateTimePicker';
import { TimeField } from '../../components/TimeField/TimeField';
import MaterialTextField from '../../components/MaterialTextField/MaterialTextField';
import { DialogControls } from '../../components/DialogControls/DialogControls';
import { getUsersEdgeLeg } from '../../selectors/screen';
import { Dialog, DialogContent, DialogTitle, DialogActions } from '../../components/material-ui/Dialog';
import { useStyles } from './useStyles';
import { Typography } from '../../components/material-ui/Typography';
import { useMobileWidth } from '../../components/hooks/useMobileWidth';
import { getDate } from '../../service/DateTimeUtils';
import { getTranslateText } from '../../service/utils';
import { useSummaryCrewModal } from './useSummaryCrewModal';

export const SUMMARY_CREW_LEG = 'SUMMARY_CREW_LEG';

export const SummaryCrewLegModal = memo(
    forwardRef((props, ref) => {
        const { title, text, legId, persId } = props;
        const usersEdgeLeg = useSelector(getUsersEdgeLeg);
        const disableStart = get(usersEdgeLeg[persId], 'start.id') !== legId;
        const disableEnd = get(usersEdgeLeg[persId], 'end.id') !== legId;
        const crew = useSelector((state) => get(state, 'crew', {}));
        const hideModal = useHideModal();
        const dispatch = useDispatch();
        const classes = useStyles();
        const { isMobilePortrait } = useMobileWidth();
        const { data, handleChangeComment, handleChangeField, isValidWorkBegin, handleSave } = useSummaryCrewModal(props, disableStart);
        const { t: translate } = useTranslation();

        const handleDisplayWarning = useCallback(() => {
            dispatch(
                showConfirmDialog({
                    title: translate('messages.editCrewDutyTimeError'),
                    message: translate('messages.startLegCannotEarlierEndPrevious'),
                    btnCancelVisibility: false,
                })
            );
        }, []);

        const actions = useMemo(
            () => [
                { label: translate('cancel'), onClick: hideModal },
                { label: translate('ok'), color: 'primary', onClick: isValidWorkBegin ? handleSave : handleDisplayWarning },
            ],
            [isValidWorkBegin, hideModal, handleSave, handleDisplayWarning, translate]
        );
        const crewInfo = (crew[persId] && getTranslateText('fullName', crew[persId])) || '';

        return (
            <Dialog open={true} ref={ref} maxWidth="md" fullScreen={isMobilePortrait}>
                <DialogTitle className={classes.title}>{translate('modal.editCrewDutyTimeFlight', { title })}</DialogTitle>
                <DialogContent className={classes.content}>
                    <Typography className={classes.crew}>{crewInfo}</Typography>
                    <Typography>{text}</Typography>
                    <div className={classes.dates}>
                        <DateTimePicker
                            name="workBegin"
                            canBeEmpty={false}
                            value={getDate(data.workBegin)}
                            labelTextDate={translate('modal.startLeg')}
                            onChange={handleChangeField}
                            disabled={disableStart}
                        />
                        <DateTimePicker
                            name="workEnd"
                            canBeEmpty={false}
                            value={getDate(data.workEnd)}
                            labelTextDate={translate('modal.endLeg')}
                            onChange={handleChangeField}
                            disabled={disableEnd}
                        />
                    </div>
                    <div className={classes.dates}>
                        <TimeField
                            key="biologicalNight"
                            name="biologicalNight"
                            onEditField={handleChangeField}
                            defaultValue={data.biologicalNight}
                            className={classes.biologicalNight}
                            label={translate('modal.bioNight')}
                        />
                        <TimeField
                            key="independent"
                            name="independent"
                            onEditField={handleChangeField}
                            defaultValue={data.independent}
                            className={classes.independent}
                            label={translate('modal.independentFlight')}
                        />
                    </div>
                    <MaterialTextField
                        InputProps={{
                            classes: { root: classes.commentInput },
                        }}
                        placeholder={translate('labels.additionalInfo')}
                        name="comment"
                        onChange={handleChangeComment}
                        key="comment"
                        label={translate('labels.additionalInfo')}
                        fullWidth={true}
                        multiline={true}
                        value={data.comment}
                        className={classes.comment}
                        minRows={1}
                        maxRows={5}
                    />
                </DialogContent>
                <DialogActions className={classes.actions}>
                    <DialogControls actions={actions} />
                </DialogActions>
            </Dialog>
        );
    })
);
